import React from 'react';
import { Button, IconButton } from '@mui/material';
import { 
    Add, 
    Edit, 
    Delete, 
    Save, 
    Cancel, 
    Visibility,
    Print,
    PhotoCamera,
    Search,
    Person,
    ArrowBack,
    ArrowForward,
    Check,
    Close,
    EventBusy
} from '@mui/icons-material';

// Standard Button Variants
export const ActionButton = ({ 
    variant = 'contained', 
    color = 'primary', 
    startIcon, 
    children, 
    onClick,
    disabled = false,
    size = 'medium',
    ...props 
}) => (
    <Button
        variant={variant}
        color={color}
        startIcon={startIcon}
        onClick={onClick}
        disabled={disabled}
        size={size}
        {...props}
    >
        {children}
    </Button>
);

// Standard IconButton
export const ActionIconButton = ({ 
    color = 'primary', 
    icon: IconComponent, 
    onClick,
    disabled = false,
    size = 'medium',
    ...props 
}) => (
    <IconButton
        color={color}
        onClick={onClick}
        disabled={disabled}
        size={size}
        {...props}
    >
        <IconComponent />
    </IconButton>
);

// Predefined Common Actions
export const AddButton = ({ onClick, children = 'Aggiungi', variant = 'contained', ...props }) => (
    <ActionButton
        variant={variant}
        color="primary"
        startIcon={<Add />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const EditButton = ({ onClick, children = 'Modifica', variant = 'outlined', ...props }) => (
    <ActionButton
        variant={variant}
        color="primary"
        startIcon={<Edit />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const EditIconButton = ({ onClick, color = 'primary', ...props }) => (
    <ActionIconButton
        color={color}
        icon={Edit}
        onClick={onClick}
        {...props}
    />
);

export const DeleteButton = ({ onClick, children = 'Elimina', variant = 'outlined', ...props }) => (
    <ActionButton
        variant={variant}
        color="error"
        startIcon={<Delete />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const DeleteIconButton = ({ onClick, color = 'error', ...props }) => (
    <ActionIconButton
        color={color}
        icon={Delete}
        onClick={onClick}
        {...props}
    />
);

export const SaveButton = ({ onClick, children = 'Salva', variant = 'contained', ...props }) => (
    <ActionButton
        variant={variant}
        color="primary"
        startIcon={<Save />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const CancelButton = ({ onClick, children = 'Annulla', variant = 'outlined', ...props }) => (
    <ActionButton
        variant={variant}
        color="primary"
        startIcon={<Cancel />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const ViewButton = ({ onClick, children = 'Visualizza', variant = 'outlined', ...props }) => (
    <ActionButton
        variant={variant}
        color="primary"
        startIcon={<Visibility />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const ViewIconButton = ({ onClick, color = 'primary', ...props }) => (
    <ActionIconButton
        color={color}
        icon={Visibility}
        onClick={onClick}
        {...props}
    />
);

export const BackButton = ({ onClick, children = 'Indietro', variant = 'contained', ...props }) => (
    <ActionButton
        variant={variant}
        color="primary"
        startIcon={<ArrowBack />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const PrintButton = ({ onClick, children = 'Stampa', variant = 'outlined', ...props }) => (
    <ActionButton
        variant={variant}
        color="primary"
        startIcon={<Print />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const CameraIconButton = ({ onClick, color = 'primary', ...props }) => (
    <ActionIconButton
        color={color}
        icon={PhotoCamera}
        onClick={onClick}
        {...props}
    />
);

export const SearchIconButton = ({ onClick, color = 'primary', ...props }) => (
    <ActionIconButton
        color={color}
        icon={Search}
        onClick={onClick}
        {...props}
    />
);

export const ConfirmButton = ({ onClick, children = 'Conferma', variant = 'contained', ...props }) => (
    <ActionButton
        variant={variant}
        color="success"
        startIcon={<Check />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const EndButton = ({ onClick, children = 'Termina', variant = 'outlined', ...props }) => (
    <ActionButton
        variant={variant}
        color="error"
        startIcon={<EventBusy />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const CloseButton = ({ onClick, children = 'Chiudi', variant = 'contained', ...props }) => (
    <ActionButton
        variant={variant}
        color="primary"
        startIcon={<Close />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const UpdateButton = ({ onClick, children = 'Aggiorna', variant = 'contained', ...props }) => (
    <ActionButton
        variant={variant}
        color="primary"
        startIcon={<Save />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

export const NextButton = ({ onClick, children = 'Avanti', variant = 'contained', ...props }) => (
    <ActionButton
        variant={variant}
        color="primary"
        endIcon={<ArrowForward />}
        onClick={onClick}
        {...props}
    >
        {children}
    </ActionButton>
);

// Standard Grouped Actions for Tables
export const TableActionButtons = ({ 
    onView, 
    onEdit, 
    onDelete, 
    showView = true, 
    showEdit = true, 
    showDelete = true,
    size = 'small'
}) => (
    <>
        {showView && <ViewIconButton onClick={onView} size={size} />}
        {showEdit && <EditIconButton onClick={onEdit} size={size} />}
        {showDelete && <DeleteIconButton onClick={onDelete} size={size} />}
    </>
);

// Dialog Action Buttons
export const DialogActions = ({ 
    onCancel, 
    onConfirm, 
    cancelText = 'Annulla', 
    confirmText = 'Conferma',
    confirmColor = 'primary',
    confirmVariant = 'contained'
}) => (
    <>
        <CancelButton onClick={onCancel} variant="text">
            {cancelText}
        </CancelButton>
        <ActionButton 
            onClick={onConfirm}
            variant={confirmVariant}
            color={confirmColor}
        >
            {confirmText}
        </ActionButton>
    </>
);

export default {
    ActionButton,
    ActionIconButton,
    AddButton,
    EditButton,
    EditIconButton,
    DeleteButton,
    DeleteIconButton,
    SaveButton,
    CancelButton,
    ViewButton,
    ViewIconButton,
    BackButton,
    NextButton,
    PrintButton,
    CameraIconButton,
    SearchIconButton,
    ConfirmButton,
    CloseButton,
    EndButton,
    UpdateButton,
    TableActionButtons,
    DialogActions
};